<template>
    <div>
        <v-card outlined class="pa-4 mb-4">
            <div class="d-flex flex-wrap align-center justify-space-between">
                <div>
                    <div class="secao-titulo">Despesas de {{ mesFormatado }}</div>
                    <div class="caption grey--text text--darken-1">
                        Classifique cada saída para enxergar o custo real da operação.
                        Só as despesas com status "Pago" entram no lucro do mês.
                    </div>
                </div>

                <v-btn color="teal darken-2" dark class="mt-3 mt-md-0" @click="abrirNova">
                    <v-icon left>mdi-plus</v-icon>
                    Nova despesa
                </v-btn>
            </div>
        </v-card>

        <div class="resumo-grid mb-4">
            <div v-for="item in resumo" :key="item.rotulo" class="resumo-card">
                <div class="resumo-card__label">{{ item.rotulo }}</div>
                <div class="resumo-card__value">{{ formatarMoeda(item.valor) }}</div>
            </div>
        </div>

        <v-card outlined>
            <v-data-table
                :headers="headers"
                :items="despesas"
                :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                dense
                sort-by="data"
                no-data-text="Nenhuma despesa lançada neste mês."
            >
                <template v-slot:item.data="{ item }">
                    {{ formatarData(item.data) }}
                </template>

                <template v-slot:item.descricao="{ item }">
                    <div class="font-weight-medium">{{ item.descricao || '—' }}</div>
                    <div class="caption grey--text">{{ item.fornecedor || 'Sem fornecedor' }}</div>
                </template>

                <template v-slot:item.tipo="{ item }">
                    <v-chip x-small :color="item.tipo === 'Fixa' ? 'indigo lighten-5' : 'orange lighten-5'"
                        :text-color="item.tipo === 'Fixa' ? 'indigo darken-2' : 'orange darken-4'">
                        {{ item.tipo }}
                    </v-chip>
                </template>

                <template v-slot:item.valor="{ item }">
                    <span class="font-weight-bold">{{ formatarMoeda(item.valor) }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                    <v-chip x-small :color="item.status === 'Pago' ? 'green lighten-4' : 'red lighten-4'"
                        :text-color="item.status === 'Pago' ? 'green darken-3' : 'red darken-3'">
                        {{ item.status }}
                    </v-chip>
                </template>

                <template v-slot:item.acoes="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn icon small color="primary" title="Editar" @click="abrirEdicao(item)">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small color="red" title="Excluir" @click="remover(item)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </v-card>

        <v-dialog v-model="dialog" max-width="640">
            <v-card>
                <v-card-title class="headline">
                    {{ despesa.id ? 'Editar despesa' : 'Nova despesa' }}
                </v-card-title>

                <v-card-text>
                    <v-form ref="form">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="despesa.data"
                                    type="date"
                                    label="Data"
                                    outlined
                                    dense
                                    :rules="[obrigatorio]"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="despesa.descricao"
                                    label="Descrição"
                                    outlined
                                    dense
                                    :rules="[obrigatorio]"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-combobox
                                    v-model="despesa.categoria"
                                    :items="config.categoriasDespesa"
                                    label="Categoria"
                                    hint="Taxa de crédito registra o total da maquininha sem classificar cada receita"
                                    persistent-hint
                                    outlined
                                    dense
                                ></v-combobox>
                                <v-alert
                                    v-if="despesa.categoria === categoriaTaxaCredito && taxaCreditoAutomaticaAtiva"
                                    dense
                                    text
                                    type="warning"
                                    class="caption mt-2 mb-0"
                                >
                                    A taxa automática de crédito também está configurada. Deixe-a em 0% para não descontar duas vezes o mesmo período.
                                </v-alert>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="despesa.fornecedor"
                                    label="Fornecedor"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="despesa.tipo"
                                    :items="tiposDespesa"
                                    label="Tipo"
                                    hint="Fixa repete todo mês; variável acompanha o movimento"
                                    persistent-hint
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="despesa.apropriacao"
                                    :items="apropriacoes"
                                    label="Direta / Indireta"
                                    hint="Direta está ligada ao serviço; indireta mantém a operação"
                                    persistent-hint
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="despesa.pagamento"
                                    :items="config.formasPagamento"
                                    label="Forma de pagamento"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <campo-valor
                                    v-model="despesa.valor"
                                    prefix="R$"
                                    label="Valor"
                                    outlined
                                    dense
                                    :rules="[valorValido]"
                                ></campo-valor>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="despesa.status"
                                    :items="statusDespesa"
                                    label="Status"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="despesa.vencimento"
                                    type="date"
                                    label="Vencimento"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialog = false">Cancelar</v-btn>
                    <v-btn color="primary" :loading="salvando" @click="salvar">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { salvarDespesa, removerDespesa } from '@/services/financeiro'
import {
    STATUS_DESPESA,
    TIPOS_DESPESA,
    APROPRIACOES_DESPESA,
    CATEGORIA_TAXA_CREDITO,
    formatarMoeda,
    numero
} from '@/services/financeiroCalculos'
import CampoValor from './CampoValor'

export default {
    components: { CampoValor },

    props: ['mes', 'despesas', 'config', 'usuario'],

    data(){
        return {
            dialog: false,
            salvando: false,
            despesa: this.despesaVazia(),
            categoriaTaxaCredito: CATEGORIA_TAXA_CREDITO,
            statusDespesa: STATUS_DESPESA,
            tiposDespesa: TIPOS_DESPESA,
            apropriacoes: APROPRIACOES_DESPESA,
            headers: [
                { text: 'Data', value: 'data', width: 120 },
                { text: 'Descrição', value: 'descricao' },
                { text: 'Categoria', value: 'categoria' },
                { text: 'Tipo', value: 'tipo', width: 110 },
                { text: 'Apropriação', value: 'apropriacao', width: 130 },
                { text: 'Valor', value: 'valor', width: 130 },
                { text: 'Status', value: 'status', width: 120 },
                { text: '', value: 'acoes', sortable: false, align: 'end' }
            ]
        }
    },

    computed: {
        mesFormatado(){
            if(!this.mes){
                return ''
            }
            const [ano, mes] = this.mes.split('-')
            const nomes = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro']
            return `${nomes[Number(mes) - 1]}/${ano}`
        },

        taxaCreditoAutomaticaAtiva(){
            return numero((this.config || {}).taxaCredito) > 0
        },

        resumo(){
            const pagas = this.despesas.filter((item) => item.status === 'Pago')
            const somar = (lista) => lista.reduce((acc, item) => acc + numero(item.valor), 0)

            return [
                { rotulo: 'Pagas no mês', valor: somar(pagas) },
                { rotulo: 'Em aberto', valor: somar(this.despesas.filter((item) => item.status === 'Em aberto')) },
                { rotulo: 'Fixas pagas', valor: somar(pagas.filter((item) => item.tipo === 'Fixa')) },
                { rotulo: 'Variáveis pagas', valor: somar(pagas.filter((item) => item.tipo === 'Variável')) }
            ]
        }
    },

    methods: {
        formatarMoeda,

        despesaVazia(){
            return {
                data: '',
                descricao: '',
                categoria: '',
                tipo: 'Fixa',
                apropriacao: 'Indireta',
                fornecedor: '',
                pagamento: '',
                valor: 0,
                status: 'Pago',
                vencimento: ''
            }
        },

        obrigatorio(valor){
            return !!valor || 'Campo obrigatório'
        },

        valorValido(valor){
            return numero(valor) > 0 || 'Informe um valor maior que zero'
        },

        formatarData(data){
            if(!data){
                return '—'
            }
            const [ano, mes, dia] = String(data).split('-')
            return `${dia}/${mes}/${ano}`
        },

        abrirNova(){
            this.despesa = this.despesaVazia()
            this.despesa.data = `${this.mes}-01`
            if(this.$refs.form){
                this.$refs.form.resetValidation()
            }
            this.dialog = true
        },

        abrirEdicao(item){
            this.despesa = { ...item }
            if(this.$refs.form){
                this.$refs.form.resetValidation()
            }
            this.dialog = true
        },

        async salvar(){
            if(!this.$refs.form.validate()){
                return
            }

            this.salvando = true
            try {
                await salvarDespesa(this.despesa, this.usuario)
                this.$toasted.global.defaultSuccess('Despesa salva!')
                this.dialog = false
                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.salvando = false
            }
        },

        async remover(item){
            if(!confirm(`Excluir a despesa "${item.descricao}"?`)){
                return
            }

            try {
                await removerDespesa(item.id)
                this.$toasted.global.defaultSuccess('Despesa excluída!')
                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            }
        }
    }
}
</script>

<style scoped>
.secao-titulo {
    font-size: 18px;
    font-weight: 800;
    color: #14312d;
}

.resumo-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.resumo-card {
    padding: 14px 16px;
    border-radius: 14px;
    background: linear-gradient(180deg, #f7fbfa 0%, #eef5f4 100%);
    border: 1px solid #d5e5e2;
}

.resumo-card__label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #5f7a76;
    margin-bottom: 6px;
}

.resumo-card__value {
    font-size: 20px;
    font-weight: 800;
    color: #14312d;
}

@media (max-width: 960px) {
    .resumo-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
